import type { ThemeDefinition, ThemeItem, ThemeKey } from '../../core/domain'
import { STANDARD_THEME_KEYS } from './keys'
import { SOCCER_ITEMS } from './items/soccer'
import { MTG_ITEMS } from './items/mtg'
import { TOURIST_ITEMS } from './items/tourist'
import { ROCKROLL_ITEMS } from './items/rockroll'
import { MUST_VISIT_CITIES_ITEMS } from './items/mustVisitCities'
import { ANIMAL_ITEMS } from './items/animals'
import { NATURAL_WONDER_ITEMS } from './items/naturalWonders'

function withSource(items: ThemeItem[], sourceId: string): ThemeItem[] {
  return items.map((item) => ({
    ...item,
    imageUrl: null,
    sourceId: item.sourceId ?? sourceId,
    sourcePath: item.sourcePath ?? item.name
  }))
}

const SOCCER_CATALOG = withSource(SOCCER_ITEMS, 'wikipedia-search')
const MTG_CATALOG = withSource(MTG_ITEMS, 'scryfall-search')
const TOURIST_CATALOG = withSource(TOURIST_ITEMS, 'wikivoyage-search')
const ROCKROLL_CATALOG = withSource(ROCKROLL_ITEMS, 'wikipedia-search')

export const THEME_NAME_KEYS: Record<ThemeKey, string> = {
  standard50: 'standard',
  standard100: 'standard',
  standard150: 'standard',
  standard250: 'standard',
  standard400: 'standard',
  standard500: 'standard',
  mustVisitCities500: 'mustVisitCities',
  animals500: 'animals',
  naturalWonders500: 'naturalWonders',
  soccer50: 'soccer',
  pokemon151: 'pokemonOriginal151',
  pokemon100: 'pokemon',
  pokemon250: 'pokemon',
  pokemon350: 'pokemon',
  pokemon500: 'pokemon',
  mtgrevised: 'mtgRevised',
  mtg100: 'mtgCommander',
  mtg250: 'mtgCommander',
  mtg350: 'mtgCommander',
  mtg500: 'mtgCommander',
  unesco100: 'unesco',
  unesco250: 'unesco',
  unesco350: 'unesco',
  unesco500: 'unesco',
  castle100: 'castle',
  castle250: 'castle',
  castle350: 'castle',
  castle500: 'castle',
  inventions100: 'inventions',
  inventions250: 'inventions',
  inventions350: 'inventions',
  inventions500: 'inventions',
  compounds100: 'compounds',
  compounds250: 'compounds',
  compounds350: 'compounds',
  compounds500: 'compounds',
  cosmos100: 'cosmos',
  cosmos250: 'cosmos',
  cosmos350: 'cosmos',
  cosmos500: 'cosmos',
  backpacker100: 'backpacker',
  backpacker250: 'backpacker',
  backpacker350: 'backpacker',
  backpacker500: 'backpacker',
  tourist100: 'tourist',
  tourist250: 'tourist',
  tourist326: 'tourist',
  artists100: 'artists',
  artists250: 'artists',
  artists350: 'artists',
  artists500: 'artists',
  rockroll100: 'rockroll',
  rockroll250: 'rockroll',
  rockroll350: 'rockroll',
  rockroll432: 'rockroll',
  turningpoints100: 'turningpoints',
  turningpoints250: 'turningpoints',
  turningpoints350: 'turningpoints',
  turningpoints500: 'turningpoints',
  architecture100: 'architecture',
  architecture250: 'architecture',
  architecture350: 'architecture',
  architecture500: 'architecture',
  botanical100: 'botanical',
  botanical250: 'botanical',
  botanical350: 'botanical',
  botanical500: 'botanical',
  marine100: 'marine',
  marine250: 'marine',
  marine350: 'marine',
  marine500: 'marine'
}

export const STANDALONE_THEME_KEYS = new Set<ThemeKey>([
  'mustVisitCities500',
  'animals500',
  'naturalWonders500',
  'pokemon151',
  'mtgrevised'
])

export const RESTRICTED_THEME_FAMILIES = ['pokemon', 'mtg'] as const

function buildSeries(options: {
  count: number
  prefix: string
  factPrefix: string
  sourceId?: string
}): ThemeItem[] {
  return Array.from({ length: options.count }, (_, i) => {
    const n = i + 1
    return {
      name: `${options.prefix} ${n}`,
      imageUrl: null,
      fact: `${options.factPrefix} ${n}.`,
      sourceId: options.sourceId,
      sourcePath: `${n}`
    }
  })
}

const STANDARD_CATALOG: ThemeItem[] = Array.from({ length: 500 }, (_, index) => ({
  name: `#${index + 1}`,
  imageUrl: null
}))

function createSizedTheme(options: {
  key: ThemeKey
  name: string
  size: number
  accent: string
  accentSoft: string
  hero: string
  items: ThemeItem[]
  sourceId?: string
}): ThemeDefinition {
  return {
    key: options.key,
    name: options.name,
    size: options.size,
    accent: options.accent,
    accentSoft: options.accentSoft,
    hero: options.hero,
    items: options.items.slice(0, options.size),
    sourceId: options.sourceId
  }
}

export const THEME_DEFINITIONS: Record<ThemeKey, ThemeDefinition> = {
  standard50: createSizedTheme({
    key: 'standard50',
    name: 'Standard Album 50',
    size: 50,
    accent: '#2563eb',
    accentSoft: '#dbeafe',
    hero: 'A numbers-only album with 50 stickers.',
    items: STANDARD_CATALOG
  }),
  standard100: createSizedTheme({
    key: 'standard100',
    name: 'Standard Album 100',
    size: 100,
    accent: '#2563eb',
    accentSoft: '#dbeafe',
    hero: 'A numbers-only album with 100 stickers.',
    items: STANDARD_CATALOG
  }),
  standard150: createSizedTheme({
    key: 'standard150',
    name: 'Standard Album 150',
    size: 150,
    accent: '#2563eb',
    accentSoft: '#dbeafe',
    hero: 'A numbers-only album with 150 stickers.',
    items: STANDARD_CATALOG
  }),
  standard250: createSizedTheme({
    key: 'standard250',
    name: 'Standard Album 250',
    size: 250,
    accent: '#2563eb',
    accentSoft: '#dbeafe',
    hero: 'A numbers-only album with 250 stickers.',
    items: STANDARD_CATALOG
  }),
  standard400: createSizedTheme({
    key: 'standard400',
    name: 'Standard Album 400',
    size: 400,
    accent: '#2563eb',
    accentSoft: '#dbeafe',
    hero: 'A numbers-only album with 400 stickers.',
    items: STANDARD_CATALOG
  }),
  standard500: createSizedTheme({
    key: 'standard500',
    name: 'Standard Album 500',
    size: 500,
    accent: '#2563eb',
    accentSoft: '#dbeafe',
    hero: 'A numbers-only album with 500 stickers.',
    items: STANDARD_CATALOG
  }),
  mustVisitCities500: {
    key: 'mustVisitCities500',
    name: '500 Must-Visit Cities in the World',
    size: 500,
    accent: '#b4432d',
    accentSoft: '#fde9df',
    hero: 'A journey through 500 cities chosen for their culture, history and travel appeal.',
    items: MUST_VISIT_CITIES_ITEMS,
    sourceId: 'wikivoyage-search'
  },
  animals500: {
    key: 'animals500',
    name: '500 Animals',
    size: 500,
    accent: '#28735a',
    accentSoft: '#ddf4e8',
    hero: 'Explore 500 animals across habitats, continents and major branches of animal life.',
    items: ANIMAL_ITEMS,
    sourceId: 'wikipedia-search'
  },
  naturalWonders500: {
    key: 'naturalWonders500',
    name: '500 Natural Wonders',
    size: 500,
    accent: '#0b6e75',
    accentSoft: '#def4f2',
    hero: 'Journey through 500 extraordinary landscapes and natural phenomena around the world.',
    items: NATURAL_WONDER_ITEMS,
    sourceId: 'wikipedia-search'
  },
  soccer50: {
    key: 'soccer50',
    name: 'Soccer 50',
    size: 50,
    accent: '#1f8a70',
    accentSoft: '#d8fff1',
    hero: 'A compact collection of 50 football personalities and milestones.',
    items: SOCCER_CATALOG.slice(0, 50),
    sourceId: 'wikipedia-search'
  },
  pokemon151: createSizedTheme({
    key: 'pokemon151',
    name: 'Pokemon Original 151',
    size: 151,
    accent: '#dc2626',
    accentSoft: '#fee2e2',
    hero: 'A text-only reference album for the original 151 creature species, with no official artwork or media.',
    items: buildSeries({
      count: 151,
      prefix: 'Pokemon Species',
      factPrefix: 'Pokedex reference',
      sourceId: 'pokeapi-species'
    }),
    sourceId: 'pokeapi-species'
  }),
  pokemon100: createSizedTheme({
    key: 'pokemon100',
    name: 'Pokemon 100',
    size: 100,
    accent: '#dc2626',
    accentSoft: '#fee2e2',
    hero: 'A text-only creature species reference album with no official artwork or media.',
    items: buildSeries({
      count: 500,
      prefix: 'Pokemon Species',
      factPrefix: 'Pokedex reference',
      sourceId: 'pokeapi-species'
    }),
    sourceId: 'pokeapi-species'
  }),
  pokemon250: createSizedTheme({
    key: 'pokemon250',
    name: 'Pokemon 250',
    size: 250,
    accent: '#dc2626',
    accentSoft: '#fee2e2',
    hero: 'A text-only creature species reference album with no official artwork or media.',
    items: buildSeries({
      count: 500,
      prefix: 'Pokemon Species',
      factPrefix: 'Pokedex reference',
      sourceId: 'pokeapi-species'
    }),
    sourceId: 'pokeapi-species'
  }),
  pokemon350: createSizedTheme({
    key: 'pokemon350',
    name: 'Pokemon 350',
    size: 350,
    accent: '#dc2626',
    accentSoft: '#fee2e2',
    hero: 'A text-only creature species reference album with no official artwork or media.',
    items: buildSeries({
      count: 500,
      prefix: 'Pokemon Species',
      factPrefix: 'Pokedex reference',
      sourceId: 'pokeapi-species'
    }),
    sourceId: 'pokeapi-species'
  }),
  pokemon500: createSizedTheme({
    key: 'pokemon500',
    name: 'Pokemon 500',
    size: 500,
    accent: '#dc2626',
    accentSoft: '#fee2e2',
    hero: 'A text-only creature species reference album with no official artwork or media.',
    items: buildSeries({
      count: 500,
      prefix: 'Pokemon Species',
      factPrefix: 'Pokedex reference',
      sourceId: 'pokeapi-species'
    }),
    sourceId: 'pokeapi-species'
  }),
  mtgrevised: {
    key: 'mtgrevised',
    name: 'MTG Revised Edition',
    size: 306,
    accent: '#2d3a8c',
    accentSoft: '#e6ebff',
    hero: 'A text-only collection for Revised Edition references and notes.',
    items: buildSeries({
      count: 306,
      prefix: 'Revised Card',
      factPrefix: 'Revised set reference',
      sourceId: 'scryfall-revised'
    }),
    sourceId: 'scryfall-revised'
  },
  mtg100: createSizedTheme({
    key: 'mtg100',
    name: 'MTG Commander Staples 100',
    size: 100,
    accent: '#2d3a8c',
    accentSoft: '#e6ebff',
    hero: 'A text-only collection of 100 Magic: The Gathering commander staples.',
    items: MTG_CATALOG,
    sourceId: 'scryfall-search'
  }),
  mtg250: createSizedTheme({
    key: 'mtg250',
    name: 'MTG Commander Staples 250',
    size: 250,
    accent: '#2d3a8c',
    accentSoft: '#e6ebff',
    hero: 'A text-only collection of 250 Magic: The Gathering commander staples.',
    items: MTG_CATALOG,
    sourceId: 'scryfall-search'
  }),
  mtg350: createSizedTheme({
    key: 'mtg350',
    name: 'MTG Commander Staples 350',
    size: 350,
    accent: '#2d3a8c',
    accentSoft: '#e6ebff',
    hero: 'A text-only collection of 350 Magic: The Gathering commander staples.',
    items: MTG_CATALOG,
    sourceId: 'scryfall-search'
  }),
  mtg500: createSizedTheme({
    key: 'mtg500',
    name: 'MTG Commander Staples 500',
    size: 500,
    accent: '#2d3a8c',
    accentSoft: '#e6ebff',
    hero: 'A text-only collection of 500 Magic: The Gathering commander staples.',
    items: MTG_CATALOG,
    sourceId: 'scryfall-search'
  }),
  unesco100: createSizedTheme({
    key: 'unesco100',
    name: 'UNESCO 100',
    size: 100,
    accent: '#0d6e8a',
    accentSoft: '#def6ff',
    hero: 'World Heritage focused learning album curated for 100 entries.',
    items: buildSeries({
      count: 500,
      prefix: 'UNESCO Site',
      factPrefix: 'Heritage summary',
      sourceId: 'unesco'
    }),
    sourceId: 'unesco'
  }),
  unesco250: createSizedTheme({
    key: 'unesco250',
    name: 'UNESCO 250',
    size: 250,
    accent: '#0d6e8a',
    accentSoft: '#def6ff',
    hero: 'World Heritage focused learning album curated for 250 entries.',
    items: buildSeries({
      count: 500,
      prefix: 'UNESCO Site',
      factPrefix: 'Heritage summary',
      sourceId: 'unesco'
    }),
    sourceId: 'unesco'
  }),
  unesco350: createSizedTheme({
    key: 'unesco350',
    name: 'UNESCO 350',
    size: 350,
    accent: '#0d6e8a',
    accentSoft: '#def6ff',
    hero: 'World Heritage focused learning album curated for 350 entries.',
    items: buildSeries({
      count: 500,
      prefix: 'UNESCO Site',
      factPrefix: 'Heritage summary',
      sourceId: 'unesco'
    }),
    sourceId: 'unesco'
  }),
  unesco500: {
    key: 'unesco500',
    name: 'UNESCO 500',
    size: 500,
    accent: '#0d6e8a',
    accentSoft: '#def6ff',
    hero: 'World Heritage focused learning album curated for 500 entries.',
    items: buildSeries({
      count: 500,
      prefix: 'UNESCO Site',
      factPrefix: 'Heritage summary',
      sourceId: 'unesco'
    }),
    sourceId: 'unesco'
  },
  castle100: createSizedTheme({
    key: 'castle100',
    name: 'Castle and Fortress 100',
    size: 100,
    accent: '#6c4b2a',
    accentSoft: '#f8ede1',
    hero: 'Fortifications and castles across regions and eras.',
    items: buildSeries({
      count: 500,
      prefix: 'Castle or Fortress',
      factPrefix: 'Architecture note',
      sourceId: 'wikipedia-castles'
    }),
    sourceId: 'wikipedia-castles'
  }),
  castle250: createSizedTheme({
    key: 'castle250',
    name: 'Castle and Fortress 250',
    size: 250,
    accent: '#6c4b2a',
    accentSoft: '#f8ede1',
    hero: 'Fortifications and castles across regions and eras.',
    items: buildSeries({
      count: 500,
      prefix: 'Castle or Fortress',
      factPrefix: 'Architecture note',
      sourceId: 'wikipedia-castles'
    }),
    sourceId: 'wikipedia-castles'
  }),
  castle350: createSizedTheme({
    key: 'castle350',
    name: 'Castle and Fortress 350',
    size: 350,
    accent: '#6c4b2a',
    accentSoft: '#f8ede1',
    hero: 'Fortifications and castles across regions and eras.',
    items: buildSeries({
      count: 500,
      prefix: 'Castle or Fortress',
      factPrefix: 'Architecture note',
      sourceId: 'wikipedia-castles'
    }),
    sourceId: 'wikipedia-castles'
  }),
  castle500: {
    key: 'castle500',
    name: 'Castle and Fortress 500',
    size: 500,
    accent: '#6c4b2a',
    accentSoft: '#f8ede1',
    hero: 'Fortifications and castles across regions and eras.',
    items: buildSeries({
      count: 500,
      prefix: 'Castle or Fortress',
      factPrefix: 'Architecture note',
      sourceId: 'wikipedia-castles'
    }),
    sourceId: 'wikipedia-castles'
  },
  inventions100: createSizedTheme({
    key: 'inventions100',
    name: 'Inventions 100',
    size: 100,
    accent: '#ad6800',
    accentSoft: '#fff3d6',
    hero: 'Historic inventions and inventors in a concise chronology.',
    items: buildSeries({
      count: 500,
      prefix: 'Invention',
      factPrefix: 'Innovation digest',
      sourceId: 'britannica-inventions'
    }),
    sourceId: 'britannica-inventions'
  }),
  inventions250: createSizedTheme({
    key: 'inventions250',
    name: 'Inventions 250',
    size: 250,
    accent: '#ad6800',
    accentSoft: '#fff3d6',
    hero: 'Historic inventions and inventors in a medium chronology.',
    items: buildSeries({
      count: 500,
      prefix: 'Invention',
      factPrefix: 'Innovation digest',
      sourceId: 'britannica-inventions'
    }),
    sourceId: 'britannica-inventions'
  }),
  inventions350: createSizedTheme({
    key: 'inventions350',
    name: 'Inventions 350',
    size: 350,
    accent: '#ad6800',
    accentSoft: '#fff3d6',
    hero: 'Historic inventions and inventors in an expanded chronology.',
    items: buildSeries({
      count: 500,
      prefix: 'Invention',
      factPrefix: 'Innovation digest',
      sourceId: 'britannica-inventions'
    }),
    sourceId: 'britannica-inventions'
  }),
  inventions500: {
    key: 'inventions500',
    name: 'Inventions 500',
    size: 500,
    accent: '#ad6800',
    accentSoft: '#fff3d6',
    hero: 'Historic inventions and inventors in a 500-card chronology.',
    items: buildSeries({
      count: 500,
      prefix: 'Invention',
      factPrefix: 'Innovation digest',
      sourceId: 'britannica-inventions'
    }),
    sourceId: 'britannica-inventions'
  },
  compounds100: createSizedTheme({
    key: 'compounds100',
    name: 'Compounds 100',
    size: 100,
    accent: '#0e7490',
    accentSoft: '#dcf5ff',
    hero: 'Chemistry-focused album with compact compound references.',
    items: buildSeries({
      count: 550,
      prefix: 'Chemical Compound',
      factPrefix: 'Chemistry reference',
      sourceId: 'pubchem'
    }),
    sourceId: 'pubchem'
  }),
  compounds250: createSizedTheme({
    key: 'compounds250',
    name: 'Compounds 250',
    size: 250,
    accent: '#0e7490',
    accentSoft: '#dcf5ff',
    hero: 'Chemistry-focused album with medium compound references.',
    items: buildSeries({
      count: 550,
      prefix: 'Chemical Compound',
      factPrefix: 'Chemistry reference',
      sourceId: 'pubchem'
    }),
    sourceId: 'pubchem'
  }),
  compounds350: createSizedTheme({
    key: 'compounds350',
    name: 'Compounds 350',
    size: 350,
    accent: '#0e7490',
    accentSoft: '#dcf5ff',
    hero: 'Chemistry-focused album with expanded compound references.',
    items: buildSeries({
      count: 550,
      prefix: 'Chemical Compound',
      factPrefix: 'Chemistry reference',
      sourceId: 'pubchem'
    }),
    sourceId: 'pubchem'
  }),
  compounds500: {
    key: 'compounds500',
    name: 'Compounds 550',
    size: 550,
    accent: '#0e7490',
    accentSoft: '#dcf5ff',
    hero: 'A larger chemistry-focused album with 550 compounds.',
    items: buildSeries({
      count: 550,
      prefix: 'Chemical Compound',
      factPrefix: 'Chemistry reference',
      sourceId: 'pubchem'
    }),
    sourceId: 'pubchem'
  },
  cosmos100: createSizedTheme({
    key: 'cosmos100',
    name: 'Cosmos 100',
    size: 100,
    accent: '#363f9e',
    accentSoft: '#e6e9ff',
    hero: 'Objects, missions and phenomena from astronomy and space exploration.',
    items: buildSeries({
      count: 500,
      prefix: 'Cosmos Entry',
      factPrefix: 'Astronomy note',
      sourceId: 'nasa'
    }),
    sourceId: 'nasa'
  }),
  cosmos250: createSizedTheme({
    key: 'cosmos250',
    name: 'Cosmos 250',
    size: 250,
    accent: '#363f9e',
    accentSoft: '#e6e9ff',
    hero: 'Objects, missions and phenomena from astronomy and space exploration.',
    items: buildSeries({
      count: 500,
      prefix: 'Cosmos Entry',
      factPrefix: 'Astronomy note',
      sourceId: 'nasa'
    }),
    sourceId: 'nasa'
  }),
  cosmos350: createSizedTheme({
    key: 'cosmos350',
    name: 'Cosmos 350',
    size: 350,
    accent: '#363f9e',
    accentSoft: '#e6e9ff',
    hero: 'Objects, missions and phenomena from astronomy and space exploration.',
    items: buildSeries({
      count: 500,
      prefix: 'Cosmos Entry',
      factPrefix: 'Astronomy note',
      sourceId: 'nasa'
    }),
    sourceId: 'nasa'
  }),
  cosmos500: {
    key: 'cosmos500',
    name: 'Cosmos 500',
    size: 500,
    accent: '#363f9e',
    accentSoft: '#e6e9ff',
    hero: 'Objects, missions and phenomena from astronomy and space exploration.',
    items: buildSeries({
      count: 500,
      prefix: 'Cosmos Entry',
      factPrefix: 'Astronomy note',
      sourceId: 'nasa'
    }),
    sourceId: 'nasa'
  },
  backpacker100: createSizedTheme({
    key: 'backpacker100',
    name: 'Backpacker Atlas 100',
    size: 100,
    accent: '#008060',
    accentSoft: '#d9ffee',
    hero: 'Backpacking places, routes and travel references around the world.',
    items: buildSeries({
      count: 500,
      prefix: 'Backpacker Spot',
      factPrefix: 'Travel note',
      sourceId: 'wikivoyage'
    }),
    sourceId: 'wikivoyage'
  }),
  backpacker250: createSizedTheme({
    key: 'backpacker250',
    name: 'Backpacker Atlas 250',
    size: 250,
    accent: '#008060',
    accentSoft: '#d9ffee',
    hero: 'Backpacking places, routes and travel references around the world.',
    items: buildSeries({
      count: 500,
      prefix: 'Backpacker Spot',
      factPrefix: 'Travel note',
      sourceId: 'wikivoyage'
    }),
    sourceId: 'wikivoyage'
  }),
  backpacker350: createSizedTheme({
    key: 'backpacker350',
    name: 'Backpacker Atlas 350',
    size: 350,
    accent: '#008060',
    accentSoft: '#d9ffee',
    hero: 'Backpacking places, routes and travel references around the world.',
    items: buildSeries({
      count: 500,
      prefix: 'Backpacker Spot',
      factPrefix: 'Travel note',
      sourceId: 'wikivoyage'
    }),
    sourceId: 'wikivoyage'
  }),
  backpacker500: {
    key: 'backpacker500',
    name: 'Backpacker Atlas 500',
    size: 500,
    accent: '#008060',
    accentSoft: '#d9ffee',
    hero: 'Backpacking places, routes and travel references around the world.',
    items: buildSeries({
      count: 500,
      prefix: 'Backpacker Spot',
      factPrefix: 'Travel note',
      sourceId: 'wikivoyage'
    }),
    sourceId: 'wikivoyage'
  },
  tourist100: createSizedTheme({
    key: 'tourist100',
    name: 'Wonders of the World 100',
    size: 100,
    accent: '#008060',
    accentSoft: '#d9ffee',
    hero: 'Landmarks and destinations from a curated travel reference list.',
    items: TOURIST_CATALOG,
    sourceId: 'wikivoyage-search'
  }),
  tourist250: createSizedTheme({
    key: 'tourist250',
    name: 'Wonders of the World 250',
    size: 250,
    accent: '#008060',
    accentSoft: '#d9ffee',
    hero: 'Landmarks and destinations from a curated travel reference list.',
    items: TOURIST_CATALOG,
    sourceId: 'wikivoyage-search'
  }),
  tourist326: createSizedTheme({
    key: 'tourist326',
    name: 'Wonders of the World 326',
    size: 326,
    accent: '#008060',
    accentSoft: '#d9ffee',
    hero: 'The full 326-entry landmarks and destinations reference list.',
    items: TOURIST_CATALOG,
    sourceId: 'wikivoyage-search'
  }),
  artists100: createSizedTheme({
    key: 'artists100',
    name: 'Artists 100',
    size: 100,
    accent: '#9f1853',
    accentSoft: '#ffe1ee',
    hero: 'Artists across movements, centuries and geographies.',
    items: buildSeries({
      count: 500,
      prefix: 'Artist Profile',
      factPrefix: 'Art history note',
      sourceId: 'met-collection'
    }),
    sourceId: 'met-collection'
  }),
  artists250: createSizedTheme({
    key: 'artists250',
    name: 'Artists 250',
    size: 250,
    accent: '#9f1853',
    accentSoft: '#ffe1ee',
    hero: 'Artists across movements, centuries and geographies.',
    items: buildSeries({
      count: 500,
      prefix: 'Artist Profile',
      factPrefix: 'Art history note',
      sourceId: 'met-collection'
    }),
    sourceId: 'met-collection'
  }),
  artists350: createSizedTheme({
    key: 'artists350',
    name: 'Artists 350',
    size: 350,
    accent: '#9f1853',
    accentSoft: '#ffe1ee',
    hero: 'Artists across movements, centuries and geographies.',
    items: buildSeries({
      count: 500,
      prefix: 'Artist Profile',
      factPrefix: 'Art history note',
      sourceId: 'met-collection'
    }),
    sourceId: 'met-collection'
  }),
  artists500: {
    key: 'artists500',
    name: 'Artists 500',
    size: 500,
    accent: '#9f1853',
    accentSoft: '#ffe1ee',
    hero: 'Artists across movements, centuries and geographies.',
    items: buildSeries({
      count: 500,
      prefix: 'Artist Profile',
      factPrefix: 'Art history note',
      sourceId: 'met-collection'
    }),
    sourceId: 'met-collection'
  },
  rockroll100: createSizedTheme({
    key: 'rockroll100',
    name: 'Rock Anthems 100',
    size: 100,
    accent: '#9f1853',
    accentSoft: '#ffe1ee',
    hero: 'Songs and artists that shaped rock and roll history.',
    items: ROCKROLL_CATALOG,
    sourceId: 'wikipedia-search'
  }),
  rockroll250: createSizedTheme({
    key: 'rockroll250',
    name: 'Rock Anthems 250',
    size: 250,
    accent: '#9f1853',
    accentSoft: '#ffe1ee',
    hero: 'Songs and artists that shaped rock and roll history.',
    items: ROCKROLL_CATALOG,
    sourceId: 'wikipedia-search'
  }),
  rockroll350: createSizedTheme({
    key: 'rockroll350',
    name: 'Rock Anthems 350',
    size: 350,
    accent: '#9f1853',
    accentSoft: '#ffe1ee',
    hero: 'Songs and artists that shaped rock and roll history.',
    items: ROCKROLL_CATALOG,
    sourceId: 'wikipedia-search'
  }),
  rockroll432: createSizedTheme({
    key: 'rockroll432',
    name: 'Rock Anthems 432',
    size: 432,
    accent: '#9f1853',
    accentSoft: '#ffe1ee',
    hero: 'The full 432-entry rock and roll reference list.',
    items: ROCKROLL_CATALOG,
    sourceId: 'wikipedia-search'
  }),
  turningpoints100: createSizedTheme({
    key: 'turningpoints100',
    name: 'Turning Points 100',
    size: 100,
    accent: '#5a3ec8',
    accentSoft: '#ece7ff',
    hero: 'Events that shifted history, science, politics and culture.',
    items: buildSeries({
      count: 500,
      prefix: 'Turning Point',
      factPrefix: 'Historical summary',
      sourceId: 'history-encyclopedia'
    }),
    sourceId: 'history-encyclopedia'
  }),
  turningpoints250: createSizedTheme({
    key: 'turningpoints250',
    name: 'Turning Points 250',
    size: 250,
    accent: '#5a3ec8',
    accentSoft: '#ece7ff',
    hero: 'Events that shifted history, science, politics and culture.',
    items: buildSeries({
      count: 500,
      prefix: 'Turning Point',
      factPrefix: 'Historical summary',
      sourceId: 'history-encyclopedia'
    }),
    sourceId: 'history-encyclopedia'
  }),
  turningpoints350: createSizedTheme({
    key: 'turningpoints350',
    name: 'Turning Points 350',
    size: 350,
    accent: '#5a3ec8',
    accentSoft: '#ece7ff',
    hero: 'Events that shifted history, science, politics and culture.',
    items: buildSeries({
      count: 500,
      prefix: 'Turning Point',
      factPrefix: 'Historical summary',
      sourceId: 'history-encyclopedia'
    }),
    sourceId: 'history-encyclopedia'
  }),
  turningpoints500: {
    key: 'turningpoints500',
    name: 'Turning Points 500',
    size: 500,
    accent: '#5a3ec8',
    accentSoft: '#ece7ff',
    hero: 'Events that shifted history, science, politics and culture.',
    items: buildSeries({
      count: 500,
      prefix: 'Turning Point',
      factPrefix: 'Historical summary',
      sourceId: 'history-encyclopedia'
    }),
    sourceId: 'history-encyclopedia'
  },
  architecture100: createSizedTheme({
    key: 'architecture100',
    name: "Architect's Blueprint 100",
    size: 100,
    accent: '#4b5563',
    accentSoft: '#edf2f7',
    hero: 'Architectural works, schools and blueprints in a focused set.',
    items: buildSeries({
      count: 500,
      prefix: 'Architectural Work',
      factPrefix: 'Design profile',
      sourceId: 'archdaily'
    }),
    sourceId: 'archdaily'
  }),
  architecture250: createSizedTheme({
    key: 'architecture250',
    name: "Architect's Blueprint 250",
    size: 250,
    accent: '#4b5563',
    accentSoft: '#edf2f7',
    hero: 'Architectural works, schools and blueprints in a medium set.',
    items: buildSeries({
      count: 500,
      prefix: 'Architectural Work',
      factPrefix: 'Design profile',
      sourceId: 'archdaily'
    }),
    sourceId: 'archdaily'
  }),
  architecture350: createSizedTheme({
    key: 'architecture350',
    name: "Architect's Blueprint 350",
    size: 350,
    accent: '#4b5563',
    accentSoft: '#edf2f7',
    hero: 'Architectural works, schools and blueprints in an expanded set.',
    items: buildSeries({
      count: 500,
      prefix: 'Architectural Work',
      factPrefix: 'Design profile',
      sourceId: 'archdaily'
    }),
    sourceId: 'archdaily'
  }),
  architecture500: {
    key: 'architecture500',
    name: "Architect's Blueprint 500",
    size: 500,
    accent: '#4b5563',
    accentSoft: '#edf2f7',
    hero: 'Architectural works, schools and blueprints in a 500-entry study set.',
    items: buildSeries({
      count: 500,
      prefix: 'Architectural Work',
      factPrefix: 'Design profile',
      sourceId: 'archdaily'
    }),
    sourceId: 'archdaily'
  },
  botanical100: createSizedTheme({
    key: 'botanical100',
    name: 'Botanical 100',
    size: 100,
    accent: '#2f855a',
    accentSoft: '#e2f8ea',
    hero: 'Plants, families and ecosystems for a botanical learning trail.',
    items: buildSeries({
      count: 500,
      prefix: 'Botanical Entry',
      factPrefix: 'Plant profile',
      sourceId: 'kew'
    }),
    sourceId: 'kew'
  }),
  botanical250: createSizedTheme({
    key: 'botanical250',
    name: 'Botanical 250',
    size: 250,
    accent: '#2f855a',
    accentSoft: '#e2f8ea',
    hero: 'Plants, families and ecosystems for a botanical learning trail.',
    items: buildSeries({
      count: 500,
      prefix: 'Botanical Entry',
      factPrefix: 'Plant profile',
      sourceId: 'kew'
    }),
    sourceId: 'kew'
  }),
  botanical350: createSizedTheme({
    key: 'botanical350',
    name: 'Botanical 350',
    size: 350,
    accent: '#2f855a',
    accentSoft: '#e2f8ea',
    hero: 'Plants, families and ecosystems for a botanical learning trail.',
    items: buildSeries({
      count: 500,
      prefix: 'Botanical Entry',
      factPrefix: 'Plant profile',
      sourceId: 'kew'
    }),
    sourceId: 'kew'
  }),
  botanical500: {
    key: 'botanical500',
    name: 'Botanical 500',
    size: 500,
    accent: '#2f855a',
    accentSoft: '#e2f8ea',
    hero: 'Plants, families and ecosystems for a botanical learning trail.',
    items: buildSeries({
      count: 500,
      prefix: 'Botanical Entry',
      factPrefix: 'Plant profile',
      sourceId: 'kew'
    }),
    sourceId: 'kew'
  },
  marine100: createSizedTheme({
    key: 'marine100',
    name: 'Marine Life 100',
    size: 100,
    accent: '#006e99',
    accentSoft: '#dcf4ff',
    hero: 'Marine species, habitats and conservation records from around the oceans.',
    items: buildSeries({
      count: 500,
      prefix: 'Marine Species',
      factPrefix: 'Marine biology note',
      sourceId: 'noaa'
    }),
    sourceId: 'noaa'
  }),
  marine250: createSizedTheme({
    key: 'marine250',
    name: 'Marine Life 250',
    size: 250,
    accent: '#006e99',
    accentSoft: '#dcf4ff',
    hero: 'Marine species, habitats and conservation records from around the oceans.',
    items: buildSeries({
      count: 500,
      prefix: 'Marine Species',
      factPrefix: 'Marine biology note',
      sourceId: 'noaa'
    }),
    sourceId: 'noaa'
  }),
  marine350: createSizedTheme({
    key: 'marine350',
    name: 'Marine Life 350',
    size: 350,
    accent: '#006e99',
    accentSoft: '#dcf4ff',
    hero: 'Marine species, habitats and conservation records from around the oceans.',
    items: buildSeries({
      count: 500,
      prefix: 'Marine Species',
      factPrefix: 'Marine biology note',
      sourceId: 'noaa'
    }),
    sourceId: 'noaa'
  }),
  marine500: {
    key: 'marine500',
    name: 'Marine Life 500',
    size: 500,
    accent: '#006e99',
    accentSoft: '#dcf4ff',
    hero: 'Marine species, habitats and conservation records from around the oceans.',
    items: buildSeries({
      count: 500,
      prefix: 'Marine Species',
      factPrefix: 'Marine biology note',
      sourceId: 'noaa'
    }),
    sourceId: 'noaa'
  }
}

export function themeDefinitionList(): ThemeDefinition[] {
  return STANDARD_THEME_KEYS.map((key) => THEME_DEFINITIONS[key])
}

export function getThemeDefinition(key: ThemeKey): ThemeDefinition {
  return THEME_DEFINITIONS[key]
}

export function isRestrictedThemeKey(key: ThemeKey): boolean {
  return RESTRICTED_THEME_FAMILIES.some((family) => key.startsWith(family))
}
